<template>
  <div class="signin-wrap">
    <HeaderComponent />

    <div class="signin-container">
      <div class="signin-card">
        <div v-if="step === 'phone'">
          <h1>{{ $t('signIn.title') }}</h1>
          <p class="subtitle">{{ $t('signIn.subtitle') }}</p>
          <label class="fld-label" for="ph">{{ $t('signIn.phoneLabel') }}</label>
          <input
            id="ph"
            v-model="phone"
            type="tel"
            :placeholder="$t('signIn.phonePlaceholder')"
            autocomplete="tel"
            class="fld"
            @keyup.enter="requestOtp"
          />
          <button
            class="btn-primary"
            :disabled="!canRequest || loading"
            @click="requestOtp"
          >
            <span v-if="loading">{{ $t('messages.pleaseWait') }}</span>
            <span v-else>{{ $t('signIn.sendCta') }}</span>
          </button>
          <p class="guest-note">{{ $t('signIn.guestNote') }}</p>
          <p v-if="error" class="error-note">{{ error }}</p>
        </div>

        <div v-else>
          <h1>{{ $t('signIn.otpTitle') }}</h1>
          <p class="subtitle">{{ $t('form.otpHint') }}</p>
          <input
            v-model="otp"
            inputmode="numeric"
            pattern="\d{6}"
            maxlength="6"
            class="fld fld-otp"
            @keyup.enter="verifyOtp"
          />
          <button
            class="btn-primary"
            :disabled="!canVerify || loading"
            @click="verifyOtp"
          >
            <span v-if="loading">{{ $t('messages.pleaseWait') }}</span>
            <span v-else>{{ $t('form.verifyOtp') }}</span>
          </button>
          <div class="otp-links">
            <a href="javascript:void(0)" @click="step='phone'; otp=''; error=''">{{ $t('signIn.otpBackToPhone') }}</a>
            <a href="javascript:void(0)" :class="{ disabled: loading }" @click="requestOtp">{{ $t('form.resendOtp') }}</a>
          </div>
          <p v-if="error" class="error-note">{{ error }}</p>
        </div>
      </div>
    </div>

    <FooterComponent />
  </div>
</template>

<script>
/* eslint-disable */
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import { authUrl, execute } from "@/api";
import * as session from "@/utils/citizenSession";

export default {
  name: "CitizenSignIn",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      step: 'phone',
      phone: '',
      otp: '',
      loading: false,
      error: '',
    };
  },
  computed: {
    canRequest() {
      const digits = (this.phone || '').replace(/\D/g, '');
      return digits.length >= 9;
    },
    canVerify() { return (this.otp || '').length === 6; },
  },
  methods: {
    async requestOtp() {
      this.error = '';
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append('function', 'citizenRequestOtp');
        fd.append('phoneNumber', this.phone);
        const res = await execute(fd, authUrl);
        if (res.data.success) {
          this.step = 'otp';
        } else {
          this.error = res.data.message || this.$t('messages.error');
        }
      } catch (e) { this.error = this.$t('messages.error'); }
      finally { this.loading = false; }
    },
    async verifyOtp() {
      this.error = '';
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append('function', 'citizenVerifyOtp');
        fd.append('phoneNumber', this.phone);
        fd.append('otp', this.otp);
        const res = await execute(fd, authUrl);
        if (res.data.success && res.data.data && res.data.data.token) {
          session.setSession({
            token:       res.data.data.token,
            phone:       res.data.data.phone,
            displayName: res.data.data.displayName || '',
            expiresAt:   res.data.data.expiresAt,
          });
          this.$router.push('/my-account');
        } else {
          this.error = res.data.message || this.$t('messages.error');
        }
      } catch (e) { this.error = this.$t('messages.error'); }
      finally { this.loading = false; }
    },
  },
};
</script>

<style scoped>
.signin-wrap { min-height: 100vh; display: flex; flex-direction: column; }
.signin-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  background: #f6f8fa;
}
.signin-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0,0,0,.06);
  padding: 32px;
  max-width: 440px;
  width: 100%;
}
.signin-card h1 { font-size: 22px; margin: 0 0 8px; color: #1f2328; }
.signin-card .subtitle { color: #57606a; font-size: 14px; margin: 0 0 20px; line-height: 1.5; }
.fld-label { display: block; font-size: 12px; font-weight: 600; color: #57606a; margin: 0 0 6px; }
.fld {
  width: 100%;
  padding: 12px 14px;
  font-size: 15px;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
}
.fld:focus { border-color: #1f6feb; box-shadow: 0 0 0 3px rgba(31,111,235,.15); }
.fld-otp {
  letter-spacing: 8px;
  text-align: center;
  font-size: 22px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
}
.btn-primary {
  width: 100%;
  margin-top: 16px;
  padding: 12px 16px;
  background: #1f6feb;
  color: #fff;
  border: 0;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}
.btn-primary:disabled { opacity: .5; cursor: not-allowed; }
.guest-note { font-size: 12px; color: #6e7781; margin: 14px 0 0; text-align: center; }
.error-note { font-size: 13px; color: #cf222e; margin: 12px 0 0; }
.otp-links {
  display: flex;
  justify-content: space-between;
  margin-top: 14px;
  font-size: 13px;
}
.otp-links a { color: #1f6feb; text-decoration: none; }
.otp-links a.disabled { color: #8c959f; pointer-events: none; }
</style>
